import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Tip } from '@/components/shared/tip';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Listbox, type ListboxOption } from '@/components/ui/listbox';
import type { PopoverPortalTarget } from '@/components/ui/popover-portal';
import { toast } from '@/components/ui/toast';
import { api } from '@/lib/api';
import { ApiError } from '@/lib/api-errors';

export const BUILT_IN_FUND_TYPES = [
  'tithe',
  'offering',
  'first_fruit',
  'seed',
  'welfare',
  'project',
  'general',
] as const;

export type BuiltInFundType = (typeof BUILT_IN_FUND_TYPES)[number];

interface FundCustomType {
  id: string;
  name: string;
  code: string;
}

const CREATE_TYPE_VALUE = '__create_fund_type__';

const customValue = (id: string) => `custom:${id}`;

export const encodeFundTypeValue = (
  fundType: string,
  customTypeId: string | null | undefined,
): string => {
  if (fundType === 'custom' && customTypeId) return customValue(customTypeId);
  if ((BUILT_IN_FUND_TYPES as readonly string[]).includes(fundType)) return fundType;
  return 'offering';
};

export const decodeFundTypeValue = (
  value: string,
): { fundType: BuiltInFundType | 'custom'; customTypeId: string | null } => {
  if (value.startsWith('custom:')) {
    return { fundType: 'custom', customTypeId: value.slice('custom:'.length) };
  }
  return {
    fundType: (value as BuiltInFundType) || 'offering',
    customTypeId: null,
  };
};

export const fundTypeLabel = (
  fund: { fundType: string; customTypeName?: string | null },
  t: (key: string) => string,
): string => {
  if (fund.fundType === 'custom' && fund.customTypeName) return fund.customTypeName;
  return t(`fundTypes.${fund.fundType}`);
};

/** Map a fund-type filter value to list API query params. */
export const fundTypeFilterToQuery = (
  value: string,
  anyValue: string,
): { fundType?: string; customTypeId?: string } => {
  if (value === anyValue) return {};
  const decoded = decodeFundTypeValue(value);
  if (decoded.fundType === 'custom' && decoded.customTypeId) {
    return { fundType: 'custom', customTypeId: decoded.customTypeId };
  }
  return { fundType: decoded.fundType };
};

/** Built-in fund types plus each church-defined custom type — for filter dropdowns. */
export const useFundTypeFilterOptions = (anyValue: string, anyLabel: string) => {
  const { t } = useTranslation('giving');

  const customTypes = useQuery({
    queryKey: ['giving', 'fund-custom-types'],
    queryFn: () =>
      api.get<{ data: FundCustomType[] }>('/giving/fund-custom-types?pageSize=100'),
  });

  return useMemo((): ListboxOption[] => {
    const builtIn = BUILT_IN_FUND_TYPES.map((type) => ({
      value: type,
      label: t(`fundTypes.${type}`),
    }));
    const custom = (customTypes.data?.data ?? []).map((row) => ({
      value: customValue(row.id),
      label: row.name,
    }));
    return [{ value: anyValue, label: anyLabel }, ...builtIn, ...custom];
  }, [anyLabel, anyValue, customTypes.data?.data, t]);
};

export interface FundTypeSelectProps {
  value: string;
  onChange: (value: string) => void;
  popoverPortal?: PopoverPortalTarget;
  disabled?: boolean;
  id?: string;
  'aria-label'?: string;
}

/**
 * Fund type picker: built-in types, church-defined custom types, and an
 * inline path to add a new custom type.
 */
export const FundTypeSelect = ({
  value,
  onChange,
  popoverPortal = 'body',
  disabled = false,
  id,
  'aria-label': ariaLabel,
}: FundTypeSelectProps) => {
  const { t } = useTranslation('giving');
  const { t: tTips } = useTranslation('tips');
  const { t: tCommon } = useTranslation();
  const { t: tErrors } = useTranslation('errors');
  const queryClient = useQueryClient();

  const [createOpen, setCreateOpen] = useState(false);
  const [name, setName] = useState('');

  const customTypes = useQuery({
    queryKey: ['giving', 'fund-custom-types'],
    queryFn: () =>
      api.get<{ data: FundCustomType[] }>('/giving/fund-custom-types?pageSize=100'),
  });

  const options = useMemo((): ListboxOption[] => {
    const builtIn = BUILT_IN_FUND_TYPES.map((type) => ({
      value: type,
      label: t(`fundTypes.${type}`),
    }));
    const custom = (customTypes.data?.data ?? []).map((row) => ({
      value: customValue(row.id),
      label: row.name,
    }));
    return [
      ...builtIn,
      ...custom,
      {
        value: CREATE_TYPE_VALUE,
        label: t('funds.customType.create'),
        description: tTips('giving.funds.typeCreate'),
        icon: <Plus className="size-4" aria-hidden />,
      },
    ];
  }, [customTypes.data?.data, t, tTips]);

  const create = useMutation({
    mutationFn: () =>
      api.post<FundCustomType>('/giving/fund-custom-types', {
        name: name.trim(),
      }),
    onSuccess: async (row) => {
      toast.success(t('funds.customType.saved'));
      setCreateOpen(false);
      setName('');
      await queryClient.invalidateQueries({ queryKey: ['giving', 'fund-custom-types'] });
      onChange(customValue(row.id));
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  return (
    <>
      <Listbox
        id={id}
        value={value}
        onChange={(next) => {
          if (next === CREATE_TYPE_VALUE) {
            setCreateOpen(true);
            return;
          }
          onChange(next ?? 'offering');
        }}
        options={options}
        popoverPortal={popoverPortal}
        disabled={disabled || customTypes.isLoading}
        aria-label={ariaLabel}
      />

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent closeLabel={tCommon('action.close')} className="overflow-visible">
          <DialogHeader>
            <DialogTitle>{t('funds.customType.createTitle')}</DialogTitle>
          </DialogHeader>
          <form
            className="flex flex-col gap-3"
            onSubmit={(event) => {
              // Portalled dialogs still bubble through the React tree — without this,
              // saving a type also submits the surrounding fund form.
              event.preventDefault();
              event.stopPropagation();
              if (!name.trim()) return;
              create.mutate();
            }}
          >
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="fund-custom-type-name">{t('funds.customType.name')}</Label>
              <Input
                id="fund-custom-type-name"
                value={name}
                onChange={(event) => setName(event.target.value)}
                required
                autoFocus
                maxLength={100}
              />
              <Tip tipId="giving.funds.typeName" />
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setCreateOpen(false)}>
                {tCommon('action.cancel')}
              </Button>
              <Button type="submit" loading={create.isPending} disabled={name.trim().length < 2}>
                {t('funds.customType.save')}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
};
